// Client-side feature flags for apps/web.
//
// Mirrors ADR-0014 in spirit: each flag is a Vite env var (VITE_FF_*) with
// exactly one typed helper. Call sites use the helper, never the raw string.
// Default is ON unless the var is the literal string "off"; this lets the
// staging / production kill switch flip a feature off by setting the var to
// "off" in the Pages project, without needing a code change.
//
// Flags are global per environment (no per-household / per-user targeting),
// matching the server-side convention. The KV-backed targeting design from
// PROJECT.md is explicitly NOT used here either (see ADR-0014).
//
// Cleanup: per CLAUDE.md, remove a flag after two weeks stable in production
// by deleting the helper and its single call site.

function readFlag(name: string): string | undefined {
  const v = (import.meta.env as Record<string, string | undefined>)[name];
  return typeof v === "string" ? v : undefined;
}

// Toggle for the "add a missing item" affordance on MealConfirmationScreen.
// Default ON; set VITE_FF_MEAL_ADD_MISSING_ITEM=off to disable in production
// from the Cloudflare Pages env vars without a code change.
export function mealAddMissingItemEnabled(): boolean {
  return readFlag("VITE_FF_MEAL_ADD_MISSING_ITEM") !== "off";
}

// Toggle for the per-dish "add a missing ingredient" affordance inside the
// inline editor on MealConfirmationScreen. Lives under each dish's
// ingredient list and appends a new ingredient (via Syto Sense parse), then
// re-sums the dish totals (ADR-0010). Default ON; set
// VITE_FF_MEAL_ADD_MISSING_INGREDIENT=off to disable in production from the
// Cloudflare Pages env vars without a code change.
export function mealAddMissingIngredientEnabled(): boolean {
  return readFlag("VITE_FF_MEAL_ADD_MISSING_INGREDIENT") !== "off";
}

// Toggle for the per-flat-item AI swap affordance on MealConfirmationScreen.
// Lives next to Edit/Favorite/Remove inside the controls row of each FLAT
// item (the chip is hidden when item.ingredients is non-empty; dishes have
// per-ingredient swap already). REPLACES items[idx] with a freshly parsed
// estimate via Syto Sense (use case: user has "Jajka kurze surowe" and wants
// "Jajka gotowane" without rewriting kcal/macros by hand). Default ON; set
// VITE_FF_MEAL_ITEM_SWAP=off to disable in production from the Cloudflare
// Pages env vars without a code change.
export function mealItemSwapEnabled(): boolean {
  return readFlag("VITE_FF_MEAL_ITEM_SWAP") !== "off";
}

// Toggle for child-profile features (ADR-0018): profile switcher in the top
// bar, the Children screen, the "Profile rodzinne" dashboard section, and
// the childId field on the save-meal payload. Default ON; set
// VITE_FF_CHILD_PROFILES=off to disable in production from the Cloudflare
// Pages env vars without a code change.
export function mealChildProfilesEnabled(): boolean {
  return readFlag("VITE_FF_CHILD_PROFILES") !== "off";
}
